import { useFadeIn } from "@/hooks/useFadeIn";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import serviceBuy from "@/assets/service-buy-people.jpg";
import serviceSell from "@/assets/service-sell-people.jpg";
import serviceInvest from "@/assets/service-invest-people.jpg";
import serviceDesign from "@/assets/service-design-people.jpg";

const services = [
  {
    title: "Buy a Home",
    excerpt: "Guided support and local insight from search to closing.",
    href: "/buy-a-home",
    image: serviceBuy,
    label: "Buyer Path",
  },
  {
    title: "Sell Your Home",
    excerpt: "Preparation-first strategy and negotiation built to maximize your outcome.",
    href: "/sell-your-home",
    image: serviceSell,
    label: "Seller Path",
  },
  {
    title: "Invest in Real Estate",
    excerpt: "Risk-aware investment strategy and opportunity analysis for long-term growth.",
    href: "/invest",
    image: serviceInvest,
    label: "Investor Path",
  },
  {
    title: "Interior Design",
    excerpt: "Design strategy that improves daily living and market performance.",
    href: "/interior-design",
    image: serviceDesign,
    label: "Design Path",
  },
];

const ServicesSection = () => {
  const { ref, visible } = useFadeIn();

  return (
    <section className="bg-background py-12 md:py-16">
      <div className="mx-auto mb-12 max-w-wide px-5 text-center md:mb-16 md:px-12">
        <p className="font-body text-[10px] uppercase tracking-[0.24em] text-accent mb-4">Service Paths</p>
        <h2 className="font-headline text-4xl md:text-[3.5rem] lg:text-6xl text-foreground leading-[1.1]">
          How We Help You Succeed
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto mt-5 text-base md:text-lg leading-relaxed">
          Choose the path that matches your goals, timeline, and next move.
        </p>
      </div>

      <div
        ref={ref}
        className={`mx-auto grid max-w-wide grid-cols-1 gap-6 px-5 transition-all delay-200 duration-700 md:grid-cols-2 md:gap-8 md:px-12 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {services.map((service, index) => {
          return (
          <Link
            key={service.title}
            to={service.href}
            className="group relative min-h-[320px] overflow-hidden border border-border/20 shadow-[0_8px_28px_-18px_rgba(0,0,0,0.18)] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background hover:-translate-y-1 hover:shadow-[0_18px_46px_-18px_rgba(0,0,0,0.24)] md:min-h-[360px]"
          >
            <img
              src={service.image}
              alt={service.title}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
              loading="lazy"
              decoding="async"
              width={1200}
              height={900}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/20 transition-colors duration-300 group-hover:from-black/90 group-hover:via-black/62 group-hover:to-black/28" />
            <div className="relative z-10 h-full flex items-end p-6 md:p-8">
              <div className="max-w-[85%]">
                <p className="font-body text-[10px] uppercase tracking-[0.22em] text-white/80 mb-3">
                  {service.label}
                </p>
                <h3 className="font-headline leading-tight text-white mb-3 text-2xl md:text-[2rem]">
                  {service.title}
                </h3>
                <p className="text-white/90 text-sm md:text-base leading-[1.6] md:leading-[1.72]">
                  {service.excerpt}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.14em] text-accent transition-colors duration-300 group-hover:text-accent/90">
                  Explore Service
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </div>
          </Link>
          );
        })}
      </div>
    </section>
  );
};

export default ServicesSection;
